import { useState, useEffect } from 'react';
import { contractService } from '../../services/contractService';
import { supabase } from '../../services/supabaseService';
import type { Proposal } from '../../types/proposal-blocks';
import type { Contract } from '../../types/contract';

interface SendContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposal: Proposal;
}

export function SendContractModal({ isOpen, onClose, proposal }: SendContractModalProps) {
  const [clientEmail, setClientEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [existingContract, setExistingContract] = useState<Contract | null>(null);
  const [loadingContract, setLoadingContract] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    // Pre-fill from proposal's linked contact
    (async () => {
      setLoadingContract(true);
      try {
        // Check for existing contract
        const contracts = await contractService.getContractsByProposal(proposal.id);
        const active = contracts.find((c) => c.status !== 'expired');
        setExistingContract(active || null);

        // Pre-fill client info from contact
        if (proposal.client_id) {
          const { data: contact } = await supabase
            .from('contacts')
            .select('full_name, email, company_id')
            .eq('id', proposal.client_id)
            .single();
          if (contact) {
            setClientEmail(contact.email || '');
            setClientName(contact.full_name || '');
          }
        }
      } catch {
        // ignore
      }
      setLoadingContract(false);
    })();
  }, [isOpen, proposal.id, proposal.client_id]);

  if (!isOpen) return null;

  const projectTitle = proposal.content.meta.project_name || proposal.title;

  // Calculate total from content
  const milestonesBlock = proposal.content.blocks.find((b) => b.type === 'milestones') as any;
  let totalAmount = 0;
  if (milestonesBlock?.items) {
    totalAmount = milestonesBlock.items.reduce((sum: number, item: any) => {
      const amount = parseFloat((item.amount || '0').replace(/[^\d.,]/g, '').replace(',', '.'));
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
  }

  const handleSend = async () => {
    if (!clientEmail.trim()) { alert('Por favor, ingresa el email del cliente'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientEmail)) { alert('Email no valido'); return; }

    try {
      setIsSending(true);

      let contract: Contract;
      if (existingContract && existingContract.status === 'pending') {
        // Reuse existing pending contract
        contract = existingContract;
      } else {
        // Create new contract
        contract = await contractService.createContract(
          proposal.id,
          proposal.content,
          totalAmount > 0 ? totalAmount : undefined
        );
      }

      await contractService.sendContractEmail(contract, clientEmail, clientName || 'Cliente', projectTitle);

      alert(`Contrato ${contract.contract_number} enviado a ${clientEmail}`);
      setClientEmail('');
      setClientName('');
      setIsSending(false);
      onClose();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al enviar el contrato');
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 relative">
        {/* Close button */}
        <button onClick={onClose} disabled={isSending} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Enviar Contrato</h2>
          <p className="text-gray-600">
            Proyecto: <strong>{projectTitle}</strong>
          </p>
        </div>

        {/* Existing contract warning */}
        {existingContract && (
          <div className={`rounded-xl p-4 mb-4 ${existingContract.status === 'signed' ? 'bg-green-50 border border-green-200' : existingContract.status === 'sent' ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50 border border-gray-200'}`}>
            <p className="text-sm">
              {existingContract.status === 'signed' && `Este contrato ya fue firmado (${existingContract.contract_number}).`}
              {existingContract.status === 'sent' && `Ya se envio el contrato ${existingContract.contract_number}. Puedes reenviarlo.`}
              {existingContract.status === 'pending' && `Contrato ${existingContract.contract_number} pendiente de envio.`}
            </p>
          </div>
        )}

        {/* Summary */}
        {!loadingContract && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h3 className="font-bold text-sm text-gray-700 mb-2">Resumen del Contrato</h3>
            <div className="text-sm text-gray-600 space-y-1">
              {totalAmount > 0 && (
                <p><strong>Inversion:</strong> {totalAmount.toLocaleString('es-ES')} EUR + IVA</p>
              )}
              {milestonesBlock?.items && (
                <p><strong>Hitos:</strong> {milestonesBlock.items.length}</p>
              )}
            </div>
          </div>
        )}

        {/* Form */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email del Cliente <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="cliente@empresa.com"
                disabled={isSending}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Se enviara un enlace unico para que el cliente firme el contrato
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre del Cliente
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Juan Perez"
              disabled={isSending}
            />
          </div>
        </div>

        {/* Payment info */}
        <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg mb-6">
          <p className="text-sm text-blue-800">
            <strong>Esquema de Pago:</strong> 40% inicial, 30% fase intermedia, 30% entrega final.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button onClick={onClose} disabled={isSending} className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all">
            Cancelar
          </button>
          <button
            onClick={handleSend}
            disabled={isSending || !clientEmail.trim() || (existingContract?.status === 'signed')}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Enviando...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                Enviar Contrato
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
